import React, { useCallback, useEffect, useState } from "react";
import { MerchantsResponse } from "../../../../interface/types";
import useApiHook from "../../../../utils/useApi";
import DataTable, { TableColumn } from "react-data-table-component";
import Loading from "../../../../merchant_dashboard/components/loading/Loading";
import { AdminMerchant } from "../../../../interface/interfaces";
import MerchantDetailsModal from "./MerchantDetailsModal";

const MerchantTable = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [selectedMerchant, setSelectedMerchant] =
    useState<AdminMerchant | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  const { data, isLoading, makeRequest } = useApiHook<MerchantsResponse>(
    "/admin/merchants",
    {
      method: "get",
    }
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchMerchants = useCallback(async () => {
    const params = {
      page,
      size: perPage,
      search: debouncedSearch,
      status,
    };

    const response = await makeRequest(params);

    // Check for API errors
    if (response?.data?.statusCode !== "00") {
      setTotalRows(0);
    } else if (response?.data?.data) {
      setTotalRows(response.data.data.totalCount || 0);
    }
  }, [page, perPage, debouncedSearch, status]);

  useEffect(() => {
    fetchMerchants();
  }, [page, perPage, debouncedSearch, status]);

  useEffect(() => {
    makeRequest();
  }, []);

  const handleViewDetails = (merchant: AdminMerchant) => {
    setSelectedMerchant(merchant);
    setShowDetailsModal(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value);
    setPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatus("");
    setPage(1);
  };

  const columns: TableColumn<AdminMerchant>[] = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.name}</div>
      ),
      width: "300px",
    },
    {
      name: "Code",
      selector: (row) => row.code,
      sortable: true,
      cell: (row) => (
        <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
          {row.code}
        </div>
      ),
      width: "200px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === "ACTIVE"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {row.status}
        </span>
      ),
      width: "150px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-3 rounded text-xs transition-colors duration-200"
          >
            View Details
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "400px",
    },
  ];

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedMerchant(null);
  };

  return (
    <main className="py-10">
      {/* Filters Section */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
          <div className="flex-1">
            <label
              htmlFor="search"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Search
            </label>
            <input
              id="search"
              type="text"
              placeholder="Search by name or reference..."
              value={search}
              onChange={handleSearchChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="w-full md:w-48">
            <label
              htmlFor="status"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Status
            </label>
            <select
              id="status"
              value={status}
              onChange={handleStatusChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              disabled={!search && !status}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {(debouncedSearch || status) && (
          <div className="mt-3 text-sm text-gray-600">
            Showing results for:
            {debouncedSearch && (
              <span className="ml-1 font-medium">"{debouncedSearch}"</span>
            )}
            {debouncedSearch && status && <span className="mx-1">•</span>}
            {status && <span className="font-medium">{status}</span>}
          </div>
        )}
      </div>

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        progressPending={isLoading}
        progressComponent={<Loading />}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        highlightOnHover
        responsive
        persistTableHead
        // customStyles={customStyles_T}
        noDataComponent={
          <div className="p-6 text-center text-gray-500">
            No merchants found
          </div>
        }
      />

      {selectedMerchant && (
        <MerchantDetailsModal
          merchant={selectedMerchant}
          isOpen={showDetailsModal}
          onClose={handleCloseModal}
        />
      )}
    </main>
  );
};

export default MerchantTable;
